import { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { StandardDialog } from '../../../components/dialogs/base';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { apiPost } from '../../../services/apiClient';
import { Driver, CreateDriverRequest } from '../../../models/driver';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../utils/toast';
import { getDriverValidationSchema } from './driverFormConstants';
import { DriverFormFields } from './DriverFormFields';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  initialData?: Driver | null;
  onSuccess?: () => void;
};

export default function RegisterDriverModal({ isOpen, onClose, initialData, onSuccess }: Props) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<CreateDriverRequest & { coordinates: string }>({
    resolver: yupResolver(getDriverValidationSchema(t)) as any,
  });

  useEffect(() => {
    if (!isOpen) return;

    setError(null);

    if (initialData) {
      const coordinates = initialData.mainLocation
        ? `${initialData.mainLocation.latitude}, ${initialData.mainLocation.longitude}`
        : '';

      reset({
        first_name: initialData.firstName || '',
        last_name: initialData.lastName || '',
        email: initialData.email || '',
        coordinates,
        known_languages: initialData.knownLanguages || [],
        safety_certificates: initialData.safetyCertificates || [],
        base_country: initialData.baseCountry || '',
      });
    } else {
      reset({
        first_name: '',
        last_name: '',
        email: '',
        coordinates: '',
        known_languages: [],
        safety_certificates: [],
        base_country: '',
      });
    }
  }, [initialData, isOpen, reset]);

  const onSubmit = async (data: CreateDriverRequest & { coordinates: string }) => {
    setLoading(true);
    setError(null);

    try {
      // Parse coordinates string into latitude and longitude
      const coordinateParts = data.coordinates.split(',').map(part => part.trim());
      const latitude = parseFloat(coordinateParts[0]);
      const longitude = parseFloat(coordinateParts[1]);

      // Create API payload with separated coordinates
      const apiPayload: CreateDriverRequest = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        main_location: {
          latitude: latitude,
          longitude: longitude
        },
        known_languages: data.known_languages,
        safety_certificates: data.safety_certificates,
        base_country: data.base_country
      };
      await apiPost(`${process.env.REACT_APP_API_URL}/api/v1/drivers/create`, apiPayload);
      if (onSuccess) onSuccess();
      onClose();
    } catch (_) {
      toast.error(t('Failed to register driver'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <StandardDialog
      open={isOpen}
      onClose={onClose}
      title={t('Add a Driver')}
      subtitle={t('Add a new driver to your fleet')}
      maxWidth="md"
      width={{ xs: '100%', sm: '90%', md: '600px', lg: '650px' }}
      height={{ xs: '100vh', sm: '95vh', md: '90vh', lg: '85vh' }}
      error={error}
      primaryAction={{
        label: loading ? t('Registering...') : t('Register Driver'),
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: loading
      }}
      secondaryAction={{
        label: t('Cancel'),
        onClick: onClose
      }}
      contentSx={{ p: { xs: 2, sm: 2.5, md: 3 } }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={styles.formContainer}>
        <Box sx={styles.formGrid}>
          <DriverFormFields
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit)}
          />
        </Box>
      </Box>
    </StandardDialog>
  );
}

const styles = {
  formContainer: {
    flex: 1,
    overflow: 'auto'
  },
  formGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  }
};
